import React from 'react';
import { Zap, Crown, AlertCircle, ChevronRight, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { ServiceCard } from './ServiceCard';

export const BiosCard = ({ t, isClient, isAuthenticated, loading, biosStandardBase, biosDisc, openOrderModal, loginWithDiscord }) => (
  <ServiceCard testId="bios-only-card" delay={180}>
    <div className="group relative h-full rounded-2xl p-6 md:p-8 border border-[rgba(29,233,208,0.15)] bg-gradient-to-b from-zinc-900/80 to-zinc-950/90 overflow-visible flex flex-col" data-testid="bios-only-card">
      {/* Badge Exclusivité Client */}
      <div data-testid="bios-client-badge" className="service-card-badge absolute -top-3 -right-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] md:text-xs font-bold px-2.5 py-1.5 rounded-full shadow-lg flex items-center gap-1 z-10">
        <Crown className="w-3 h-3" />{t('services.cards.biosExclusivity')}
      </div>

      {/* En-tête */}
      <div className="relative flex items-center gap-3 mb-6">
        <div className="service-card-icon w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#1a2530] bg-gradient-to-br from-[#0f1620] to-[#0b0f15]">
          <Zap className="w-5 h-5 text-[#1de9d0]" strokeWidth={2} />
        </div>
        <div>
          <h3 data-testid="bios-title" className="text-lg md:text-xl font-bold text-white leading-tight">{t('services.cards.biosOnlyTitle')}</h3>
          <p className="text-xs md:text-sm text-gray-500 mt-0.5">{t('services.cards.biosOnlySubtitle')}</p>
        </div>
      </div>

      {/* Prix */}
      <div className="relative mb-4">
        <div className="flex items-end gap-2 flex-wrap">
          <span data-testid="bios-price" className="text-5xl md:text-[44px] font-extrabold leading-none tabular-nums bg-gradient-to-b from-white to-[#9fb0c0] bg-clip-text text-transparent">{biosStandardBase}</span>
          <span className="text-lg font-semibold text-gray-500 mb-1">€</span>
          <span className="text-xs md:text-sm text-gray-500 mb-1.5">{t('services.startingFrom')}</span>
        </div>
      </div>

      {/* Description */}
      <div className="relative mb-5">
        <p className="text-[#1de9d0] text-xs md:text-sm font-semibold mb-2 flex items-center gap-2"><span className="text-base">⚙️</span> {t('services.cards.biosFullHeading')}</p>
        <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
          {t('services.cards.biosLongDesc')}
        </p>
      </div>

      {/* Encart pré-requis ambre (conservé) */}
      <div className="relative mb-5 p-3 bg-amber-950/30 border border-amber-700/40 rounded-lg flex-1" data-testid="bios-prerequisite-notice">
        <p className="text-amber-300 text-[11px] md:text-xs font-semibold mb-1 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{t('services.cards.biosPrereqTitle')}</p>
        <p className="text-gray-300 text-[11px] md:text-xs leading-snug">{t('services.cards.biosPrereqText')}</p>
      </div>

      {/* Total */}
      <div data-testid="bios-total" className="relative mb-4 rounded-xl border border-[rgba(29,233,208,0.15)] bg-[rgba(29,233,208,0.03)] p-3 flex items-center justify-between">
        <span className="text-gray-400 text-[11px] md:text-xs font-medium uppercase tracking-wider">{t('services.total')}</span>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-extrabold tabular-nums text-white">{biosStandardBase}</span>
          <span className="text-base font-bold text-gray-500">€</span>
        </div>
      </div>

      {/* Bouton */}
      <Button onClick={() => isAuthenticated ? isClient && openOrderModal('bios') : loginWithDiscord()}
        disabled={loading === 'bios' || (isAuthenticated && !isClient)} data-testid="order-bios-btn"
        title={isAuthenticated && !isClient ? t('services.cards.clientsOnlyTitle') : ''}
        className={`w-full font-bold py-3.5 rounded-xl transition-all duration-300 hover:-translate-y-0.5 text-sm md:text-base ${isAuthenticated && !isClient
          ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed hover:translate-y-0' : isAuthenticated
          ? 'bg-gradient-to-r from-[#14b8c8] to-[#1de9d0] text-[#06181a] shadow-[0_0_25px_-8px_rgba(29,233,208,0.6)] hover:shadow-[0_0_35px_-6px_rgba(29,233,208,0.8)]'
          : 'bg-[#5865F2] hover:bg-[#4752C4] text-white'}`}>
        {loading === 'bios' ? <span className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t('services.loading')}</span>
          : isAuthenticated && !isClient ? <span className="flex items-center justify-center gap-1.5"><Crown className="w-4 h-4" />{t('services.cards.reservedClients')}</span>
          : isAuthenticated ? <span className="flex items-center justify-center gap-1">{t('services.orderButton')} - {biosDisc.final}€{biosDisc.discounted && <span className="ml-1 text-xs line-through opacity-60">{biosDisc.original}€</span>}<ChevronRight className="w-4 h-4" /></span>
          : <span className="flex items-center justify-center gap-2">{t('services.loginToOrder')}</span>}
      </Button>
      {isAuthenticated && !isClient && <p className="mt-2 text-[11px] text-gray-500 text-center" data-testid="bios-locked-hint">{t('services.cards.biosLockedHint')}</p>}
    </div>
  </ServiceCard>
);
